import React, {useState, useEffect, useRef} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Grid,
    CircularProgress,
    IconButton,
    Typography,
    MenuItem,
    Stack,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import CloseIcon from "@mui/icons-material/Close";
import {queryDeptMsg} from '@/services/manageService/deptService';
import {usePageCrud} from '@/hooks/usePageCrud';
import {
    createUser,
    updateUser,
    getUserById,
    queryUsersMsgByDeptId,
    activatedAccount,
    resetPassword
} from '@/services/manageService/userService';
import {InputField, SnackbarField} from '@/components/common';
import {UserInfo} from "@/store/admin/userInfo";
import { handleFormValidate, userRequiredFields } from '@/utils/formValidateUtil';

interface UserDetailProps {
    open: boolean;
    userId?: number;
    onClose: (data?: any) => void;
    isAdd?: boolean;
    onSubmit?: (data: any) => void;
}

interface UserForm {
    username: string;
    img: string;
    firstName: string;
    lastName: string;
    chineseName: string;
    mobile: string;
    email: string;
    sex: number;
    deptId: number;
    post: string;
    status: number;
    salt: string;
    supervisorId: number;
}

const initialForm: UserForm = {
    username: '',
    img: '',
    firstName: '',
    lastName: '',
    chineseName: '',
    mobile: '',
    email: '',
    sex: 0,
    deptId: 0,
    post: '',
    status: 2,
    salt: 'zhuo_yi',
    supervisorId: 0,
};

const UserDetail: React.FC<UserDetailProps> = ({open, userId, onClose, isAdd}) => {
    const theme = useTheme();
    const {canCreate, canUpdate} = usePageCrud({
        read: 'admin_user_detail',
        create: 'admin:user:create',
        update: 'admin:user:update',
        remove: 'admin:user:delete',
    });
    const [user, setUser] = useState<UserInfo | null>(null);
    const [form, setForm] = useState<UserForm>({...initialForm});
    const [formError, setFormError] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [deptOptions, setDeptOptions] = useState<{ id: string; name: string }[]>([]);
    const [supervisorOptions, setSupervisorOptions] = useState<{ id: number, name: string }[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    }>({open: false, message: '', severity: 'success'});
    const [showActivatedBtn, setShowActivatedBtn] = useState(false);
    const firstInputRef = useRef<HTMLInputElement | null>(null);
    // Superadmin entity: display-only (fixed id 14773 or username=superadmin)
    const isSuperadminReadOnly = !isAdd && (
        userId === 14773
        || String(form.username ?? '').toLowerCase() === 'superadmin'
        || String(user?.username ?? '').toLowerCase() === 'superadmin'
    );

    // -----------------------------------------------------
    // -------------- Component Initial --------------------
    // -----------------------------------------------------
    useEffect(() => {
        if (isAdd && open) {
            setLoading(true);
            setForm({...initialForm});
            setUser(null);
            setSupervisorOptions([]);
            setShowActivatedBtn(false);
            setLoading(false);
        } else if (userId && open) {
            setLoading(true);
            getUserById(userId).then((res: any) => {
                let data = Array.isArray(res) ? res[0] : res;
                setUser(data);
                setForm({...initialForm, ...data});
                setShowActivatedBtn(data.status === 2);
                if (data.deptId) {
                    queryUsersMsgByDeptId(data.deptId).then((res: any = {}) => {
                        const arr = Object.entries(res)
                            .map(([id, name]) => ({
                                id: Number(id),
                                name: String(name)
                            }));
                        setSupervisorOptions(arr);
                    });
                } else {
                    setSupervisorOptions([]);
                }
            }).finally(() => setLoading(false));
        }
    }, [userId, open, isAdd]);

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                firstInputRef.current?.focus();
            }, 0);
        }
    }, [open]);

    // -----------------------------------------------------
    // -------------- Selection Initial --------------------
    // -----------------------------------------------------

    // dept selection
    useEffect(() => {
        queryDeptMsg().then((res) => {
            setDeptOptions(Object.entries(res)
                .map(([id, name]) => ({
                    id,
                    name: String(name)
                })));
        });
    }, []);

    // supervisor selection
    useEffect(() => {
        if (form.deptId && form.deptId !== 0) {
            queryUsersMsgByDeptId(form.deptId).then((res: any = {}) => {
                const arr = Object.entries(res)
                    .map(([id, name]) => ({
                        id: Number(id),
                        name: String(name)
                    }));
                setSupervisorOptions(arr);
            });
        } else {
            setSupervisorOptions([]);
        }
    }, [form.deptId]);


    // -----------------------------------------------------
    // ----------------- Validate Handler ------------------
    // -----------------------------------------------------

    const onFormValidate = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target as HTMLInputElement;
        setForm({ ...form, [name]: value });
        handleFormValidate(name, value, userRequiredFields, setFormError);
    };

    const handleSelectChange = (name: string, value: any) => {
        setForm(f => ({...f, [name]: value}));
        if (name === 'deptId') {
            // Init supervisor when change dept
            setForm(f => ({...f, supervisorId: 0}));
        }
    };

    const handleCloseSnackbar = () => setSnackbar({...snackbar, open: false});

    // -----------------------------------------------------
    // ----------------- Button Handler --------------------
    // -----------------------------------------------------

    const validate = () => {
        const errors: any = {};
        userRequiredFields.forEach(field => {
            if (!form[field as keyof UserForm] || (typeof form[field as keyof UserForm] === 'string' && (form[field as keyof UserForm] as string).trim() === '')) {
                errors[field] = 'required fields!';
            }
        });
        setFormError(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreate = () => {
        if (!validate()) return;
        setSubmitting(true);
        const payload = {...form};
        createUser(payload)
            .then((res: any) => {
                setSubmitting(false);
                onClose({
                    type: 'create',
                    code: res.code,
                    message: res.username + ' Create successful! User Password: ' + (res.password || ''),
                });
            })
            .catch(e => {
                setSubmitting(false);
                setSnackbar({open: true, message: e?.message || 'User Create failed!', severity: 'error'});
            });
    }

    const handleSave = () => {
        if (!validate() || !userId) return;
        setSubmitting(true);
        const payload = {...form};
        updateUser(userId, payload)
            .then((res: any) => {
                setSubmitting(false);
                onClose({
                    type: 'update',
                    code: res.code,
                    message: res.username + ' Update successful!'
                });
            })
            .catch(e => {
                setSubmitting(false);
                setSnackbar({open: true, message: e?.message || 'User Update failed!', severity: 'error'});
            });
    }

    const activateUser = () => {
        if (typeof userId === 'number') {
            setSubmitting(true);
            activatedAccount([userId])
                .then((res: any) => {
                    setSubmitting(false);
                    const name = res && typeof res === 'object' ? Object.values(res)[0] : '';
                    onClose({
                        type: 'update',
                        code: 0,
                        message: name + ' Activated successful!'
                    });
                })
                .catch(e => {
                    setSubmitting(false);
                    setSnackbar({open: true, message: e?.message || 'User Activate failed!', severity: 'error'});
                });
        } else {
            setSnackbar({open: true, message: 'User ID is invalid', severity: 'error'});
        }
    }

    const handleResetPassword = () => {
        if (typeof userId !== 'number') {
            setSnackbar({ open: true, message: 'User ID is invalid', severity: 'error' });
            return;
        }
        setSubmitting(true);
        resetPassword(userId)
            .then((res: any) => {
                setSubmitting(false);
                const pwd = res && typeof res === 'object' ? (Object.values(res)[0] as string | undefined) : undefined;
                setSnackbar({
                    open: true,
                    message: pwd ? `Password reset successful! New Password: ${pwd}` : 'Password reset successful!',
                    severity: 'success'
                });
            })
            .catch((e: any) => {
                setSubmitting(false);
                setSnackbar({ open: true, message: e?.message || 'Password reset failed!', severity: 'error' });
            });
    }

    if ((isAdd && !open) || (!isAdd && !user && !loading)) return null;

    return (
        <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
            <DialogTitle sx={{m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <Typography variant="inherit">{isAdd ? 'Add User' : 'User Detail'}</Typography>
                <IconButton aria-label="close" onClick={onClose} sx={{color: theme.palette.grey[500]}}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {(loading || submitting) ? (
                    <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
                        <CircularProgress color="secondary"/>
                    </Box>
                ) : (
                    <Box component="form" autoComplete="off" sx={{pt: 1}}>
                        <Grid container spacing={2}>
                            <Grid size={{xs: 12}}>
                                <InputField
                                    label="Username"
                                    name="username"
                                    value={form.username}
                                    onChange={onFormValidate}
                                    required
                                    fullWidth
                                    error={!!formError.username}
                                    helperText={formError.username}
                                    inputRef={firstInputRef}
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                            <Grid size={{xs: 6}}>
                                <InputField
                                    label="First Name"
                                    name="firstName"
                                    value={form.firstName}
                                    onChange={onFormValidate}
                                    required
                                    fullWidth
                                    error={!!formError.firstName}
                                    helperText={formError.firstName}
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                            <Grid size={{xs: 6}}>
                                <InputField
                                    label="Last Name"
                                    name="lastName"
                                    value={form.lastName}
                                    onChange={onFormValidate}
                                    required
                                    fullWidth
                                    error={!!formError.lastName}
                                    helperText={formError.lastName}
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                            <Grid size={{xs: 12}}>
                                <InputField
                                    label="Gender"
                                    select
                                    fullWidth
                                    name="sex"
                                    value={form.sex}
                                    onChange={e => handleSelectChange('sex', Number(e.target.value))}
                                    display={isSuperadminReadOnly}
                                >
                                    <MenuItem value={0}>Unselected</MenuItem>
                                    <MenuItem value={1}>Male</MenuItem>
                                    <MenuItem value={2}>Female</MenuItem>
                                </InputField>
                            </Grid>
                            <Grid size={{xs: 12}}>
                                <InputField
                                    label="Dept"
                                    select
                                    fullWidth
                                    name="deptId"
                                    value={form.deptId}
                                    onChange={e => handleSelectChange('deptId', Number(e.target.value))}
                                    display={isSuperadminReadOnly}
                                >
                                    <MenuItem value={0}>None</MenuItem>
                                    {deptOptions.map(opt => (
                                        <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                                    ))}
                                </InputField>
                            </Grid>
                            {/* Supervisor：仅在 deptId 存在且不为 0 时渲染 */}
                            {form.deptId && form.deptId !== 0 ? (
                                <Grid size={{xs: 12}}>
                                    <InputField
                                        label="Supervisor"
                                        select
                                        fullWidth
                                        name="supervisorId"
                                        value={form.supervisorId}
                                        onChange={e => handleSelectChange('supervisorId', Number(e.target.value))}
                                        display={isSuperadminReadOnly}
                                    >
                                        <MenuItem value={0}>None</MenuItem>
                                        {supervisorOptions && supervisorOptions.map(opt => (
                                            <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                                        ))}
                                    </InputField>
                                </Grid>
                            ) : null}
                            <Grid size={{xs: 12}}>
                                <InputField
                                    label="Position"
                                    name="post"
                                    value={form.post}
                                    onChange={onFormValidate}
                                    fullWidth
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                            <Grid size={{xs: 6}}>
                                <InputField
                                    label="Chinese Name"
                                    name="chineseName"
                                    value={form.chineseName}
                                    onChange={onFormValidate}
                                    required
                                    fullWidth
                                    error={!!formError.chineseName}
                                    helperText={formError.chineseName}
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                            <Grid size={{xs: 6}}>
                                <InputField
                                    label="Image"
                                    name="img"
                                    value={form.img}
                                    onChange={onFormValidate}
                                    fullWidth
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                            <Grid size={{xs: 12}}>
                                <InputField
                                    label="Mobile"
                                    name="mobile"
                                    value={form.mobile}
                                    onChange={onFormValidate}
                                    fullWidth
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                            <Grid size={{xs: 12}}>
                                <InputField
                                    label="Email"
                                    name="email"
                                    value={form.email}
                                    onChange={onFormValidate}
                                    fullWidth
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Stack direction="row" spacing={1} sx={{px: 1, py: 0.5, flexWrap: 'wrap'}}>
                    <Button variant="outlined" onClick={onClose}
                            sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                            disabled={submitting}>Cancel</Button>
                    {isAdd ? (
                        canCreate && (
                            <Button variant="contained" color="primary" onClick={handleCreate}
                                    sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                    disabled={submitting}>
                                {submitting ? <CircularProgress size={20} color="inherit"/> : 'Create'}
                            </Button>
                        )
                    ) : (
                        <>
                            {!isSuperadminReadOnly && canUpdate && (
                                <Button onClick={handleSave} variant="contained" color="primary"
                                        sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                        disabled={submitting}>
                                    {submitting ? <CircularProgress size={20} color="inherit"/> : 'Save'}
                                </Button>
                            )}
                            {!isSuperadminReadOnly && showActivatedBtn && (
                                <Button onClick={activateUser} variant="outlined" color="primary"
                                        sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}>
                                    Activate
                                </Button>
                            )}
                            {!isSuperadminReadOnly && (
                            <Button onClick={handleResetPassword} variant="outlined" color="warning"
                                    sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                    disabled={submitting}>
                                Reset Password
                            </Button>
                            )}
                        </>
                    )}
                </Stack>
            </DialogActions>
            <SnackbarField
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity === 'success' ? 'success' : 'error'}
                onClose={handleCloseSnackbar}
            />
        </Dialog>
    );
};

export default UserDetail;
